import dynamic from 'next/dynamic'
import React, { useRef } from 'react'
import { SpaceType } from 'src/components/SearchBar/types'
import { useInView } from 'src/hooks/useInView'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import { ICoordinates } from 'src/types/graphql'
import { directoryLocationTypesForSpaceTypes } from 'src/utils/directory'
import { DirectoryGuideMode } from 'src/utils/directoryGuide'

/**
 * `ssr: false` AND a mount gate — both are needed, and neither alone works.
 *
 * Without `ssr: false`, `next/dynamic` server-renders the component and bakes
 * a `ReactDOM.preload(...)` for its chunk into the SSR HTML of every search
 * page. Without the `inView` gate below, the `import()` still fires the moment
 * the component first renders, which — because the floor renders as soon as
 * results exist, not when it is scrolled to — is hydration time.
 *
 * Together they hold the property the floor is supposed to have: nothing about
 * the directory is fetched, parsed or executed on the pages paid traffic lands
 * on until the renter actually scrolls to the bottom of the results.
 */
/**
 * Geometry of one rendered floor, measured on a live card 2026-08-23 (134px
 * tall, gap-2.5 between, ~84px of divider + note above). Used ONLY to reserve
 * space — a few pixels out is fine, a section that appears from nothing is not.
 *
 * Kept in step with `INITIAL_CAP` in DirectoryFloor.tsx.
 */
const CARD_H = 134
const CARD_GAP = 10
const FLOOR_HEADER_H = 84
const FLOOR_MAX_ROWS = 24
/** The guide's geometry (2026-10-20 screenshots, 390px): a full-weight card
 *  with its action row is ~200px, 12px apart, under a ~110px heading. */
const GUIDE_CARD_H = 200
const GUIDE_CARD_GAP = 12
const GUIDE_HEADER_H = 110

const DirectoryFloor = dynamic(() => import('./DirectoryFloor'), {
  ssr: false
})

interface DirectoryFloorMountProps {
  center?: ICoordinates
  zoom?: number
  /** The search's effective space types — translated to the directory's own
   * two categories here, so the floor can never answer a parking search with
   * storage facilities. */
  spaceTypes?: SpaceType[]
  placeId?: string
  /**
   * How many rows the floor is about to render, if the caller already knows.
   *
   * The search page does: the map fetches the same rows through the same
   * query, so by the time a renter scrolls this far the count is settled. It
   * is used ONLY to reserve height — see the sentinel below.
   */
  expectedRows?: number
  /** Desktop hover link with the map — see DirectoryCard. */
  onHoverChange?: (locationId: string | null) => void
  /** The search returned NO SpotMe listings. On that path the floor is hoisted
   *  to the top of the empty state (see SearchList), so it is the page's main
   *  answer rather than a footnote — and its heading says so. */
  zeroResults?: boolean
  /** Render as the city guide (`directoryGuideMode`) — see DirectoryFloor. */
  guideMode?: DirectoryGuideMode | null
  /** Place name for the guide heading. */
  locationName?: string
}

/**
 * Deliberately tiny and statically imported: this is the only directory code
 * on a search page until the sentinel fires.
 */
const DirectoryFloorMount = ({
  spaceTypes,
  expectedRows,
  ...rest
}: DirectoryFloorMountProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  // `null` means this vertical has no directory rows by construction
  // (warehouse / nave / land) — render nothing rather than a floor of
  // mini-bodegas under an industrial-space search.
  const locationTypes = directoryLocationTypesForSpaceTypes(spaceTypes)
  // Lookahead so the chunk AND the query resolve before the renter arrives.
  // 800px was not enough in practice — an active scroll to the bottom of the
  // results outran the dynamic import plus the round-trip, and the section
  // landed visibly. Raised to 1600px, and `DirectoryFloor` now reserves its
  // height while loading, so the two together mean nothing moves under the
  // renter. Still far short of "fetch on page load": paid landing pages ship
  // no directory code until a deliberate scroll.
  // `rootSelector`: the search page renders this inside a scrollable rail
  // (the mobile results drawer / the desktop left rail), and with the default
  // viewport root, `rootMargin` cannot see past the rail's overflow clip — the
  // 1600px lookahead simply never fired there (6.3% mobile exposure vs 22.7%
  // desktop, measured 2026-08-24). Observing against the rail itself restores
  // the lookahead; on surfaces without the marker the selector misses and the
  // observer falls back to the viewport, which is the old behavior.
  const inView = useInView(sentinelRef, {
    threshold: 0,
    rootMargin: '1600px',
    rootSelector: '[data-search-scroll-root]'
  })

  // The `directorio` namespace is deliberately NOT in any search page's
  // `serverSideTranslations` list. Adding it there would ship the whole JSON
  // into `__NEXT_DATA__` for every visitor to the site's highest-traffic
  // pages, when almost none of them scroll far enough to see the floor.
  //
  // Skipping it entirely is not an option either: a missing namespace is
  // invisible in the SSR HTML and renders raw SCREAMING_SNAKE keys in the
  // browser — the floor's divider read literally "FLOOR.TITLE · FLOOR.SUBTITLE"
  // until this was wired. `enabled: inView` means the fetch rides along with
  // the chunk, on the same scroll, and never on a pageview that stays above it.
  const i18nReady = useLazyNamespace('directorio', inView)

  // Reserve the floor's height BEFORE it mounts (Jules, 2026-08-23).
  //
  // Until `inView` flips, this div holds nothing — so the section went from
  // zero to the full height of its cards in a single frame, right as the
  // renter reached the bottom of the results. Reserving up front means the
  // floor fills a box that is already the right size and nothing moves.
  //
  // `expectedRows` is only ever a hint: when the caller cannot supply one we
  // fall back to `min-h-px`, because a zero-area IntersectionObserver target
  // is a known fragility and one pixel removes the doubt.
  const reservedHeight =
    expectedRows && expectedRows > 0
      ? rest.guideMode
        ? GUIDE_HEADER_H +
          Math.min(expectedRows, FLOOR_MAX_ROWS) *
            (GUIDE_CARD_H + GUIDE_CARD_GAP)
        : FLOOR_HEADER_H +
          Math.min(expectedRows, FLOOR_MAX_ROWS) * (CARD_H + CARD_GAP)
      : undefined

  return (
    <div
      ref={sentinelRef}
      className={reservedHeight ? undefined : 'min-h-px'}
      style={reservedHeight ? { minHeight: reservedHeight } : undefined}
      data-testid="directory-floor-sentinel"
    >
      {inView && i18nReady && locationTypes !== null && (
        <DirectoryFloor {...rest} locationTypes={locationTypes} />
      )}
    </div>
  )
}

export default React.memo(DirectoryFloorMount)
